"use client";

import { useEffect, useRef } from "react";
import { useTranslations } from "next-intl";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Toast } from "@capacitor/toast";
import { LOCALE_PATTERN } from "@/i18n/routing";

/** "/{locale}" 또는 "/{locale}/" — 로케일 루트(홈). */
const LOCALE_HOME_RE = new RegExp(`^/(${LOCALE_PATTERN})/?$`);

/**
 * 안드로이드 하드웨어 뒤로가기 처리.
 *
 * 우선순위:
 *   ① 열린 모달/시트/드로어가 있으면 → 그것부터 닫는다 (종료/이동 안 함)
 *   ② 앱 내 히스토리가 있으면 → 한 단계 뒤로
 *   ③ 홈(루트)에서 → "한 번 더 누르면 종료됩니다" 네이티브 하단 토스트.
 *      2초 안에 한 번 더 누르면 App.exitApp() 으로 종료.
 *
 * backButton 리스너를 등록하면 Capacitor 의 기본 back 동작을 완전히 대체하므로
 * 모든 분기를 직접 처리한다. iOS 는 OS 의 edge-swipe back 제스처를 쓰므로
 * 안드로이드에서만 등록한다.
 *
 * root layout(NextIntlClientProvider 내부)에 한 번만 마운트.
 */
export function AndroidBackButton() {
  const t = useTranslations("common");
  const exitMessage = t("pressBackAgainToExit");
  // 리스너를 locale 변경마다 다시 달지 않도록 메시지는 ref 로 최신값만 유지
  // (ref 갱신은 render 가 아니라 effect 에서).
  const exitMessageRef = useRef(exitMessage);
  useEffect(() => {
    exitMessageRef.current = exitMessage;
  }, [exitMessage]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (Capacitor.getPlatform() !== "android") return;

    let pressedOnce = false;
    let resetTimer: ReturnType<typeof setTimeout> | null = null;
    let listener: { remove: () => void } | undefined;

    void App.addListener("backButton", ({ canGoBack }) => {
      // ① 열린 오버레이(Radix Dialog/Sheet, Vaul Drawer 모두 role="dialog")가
      //    있으면 Esc 를 디스패치해 닫고, 이동/종료는 하지 않는다.
      const openOverlay = document.querySelector(
        '[role="dialog"][data-state="open"]',
      );
      if (openOverlay) {
        document.dispatchEvent(
          new KeyboardEvent("keydown", {
            key: "Escape",
            bubbles: true,
            cancelable: true,
          }),
        );
        return;
      }

      // ② 홈(로케일 루트)이 아니고 히스토리가 있으면 한 단계 뒤로.
      //    홈에서는 SPA 히스토리가 남아 있어도 항상 종료 분기로 보낸다 — 일반 앱처럼
      //    홈에서의 뒤로가기는 "한 번 더 누르면 종료"가 자연스럽고, canGoBack 만
      //    믿으면(로케일 리다이렉트 등으로) 종료 분기에 영영 안 닿을 수 있다.
      const path = window.location.pathname;
      const isHome = path === "/" || LOCALE_HOME_RE.test(path);
      if (!isHome && canGoBack) {
        window.history.back();
        return;
      }

      // ③ 홈(또는 히스토리 없음): 한 번 더 누르면 종료.
      if (pressedOnce) {
        if (resetTimer) clearTimeout(resetTimer);
        void App.exitApp();
        return;
      }
      pressedOnce = true;
      void Toast.show({
        text: exitMessageRef.current,
        duration: "short",
        position: "bottom",
      }).catch(() => {
        // 토스트 플러그인 미탑재(구버전 빌드) 시에도 종료 로직은 유지.
      });
      resetTimer = setTimeout(() => {
        pressedOnce = false;
      }, 2000);
    }).then((handle) => {
      listener = handle;
    });

    return () => {
      if (resetTimer) clearTimeout(resetTimer);
      listener?.remove();
    };
  }, []);

  return null;
}
